import React, { ReactElement } from 'react';
import invariant from 'tiny-invariant';

import { resolveComponent } from './resolveComponent';

import { NodeData, SerializedNode, ReducedComp, ReduceCompType, DesignSettings, Account } from '../interfaces';
import { Resolver } from '../interfaces';
import { ERROR_DESERIALIZE_COMPONENT_NOT_IN_RESOLVER } from './constants';

type DeserializedType = JSX.Element & { name: string };
/* eslint-disable */
// @ts-ignore
const restoreType = (type: ReduceCompType, resolver: Resolver) =>
    typeof type === 'object' && type.resolvedName
        ? resolver[type.resolvedName]
        : null

export const deserializeComp = (data: ReducedComp, resolver: Resolver, index?: number): DeserializedType | void => {
    let { type, props } = data;
    const main = restoreType(type, resolver);

    if (!main) {
        return;
    }

    props = Object.keys(props || {}).reduce((result: Record<string, unknown>, key) => {
        const prop = props[key];
        if (prop === null || prop === undefined) {
            result[key] = null;
        } else if (typeof prop === 'object' && (prop as ReduceCompType).resolvedName) {
            result[key] = deserializeComp(prop as ReducedComp, resolver);
        } else if (key === 'children' && Array.isArray(prop)) {
            result[key] = prop.map((child) => {
                if (typeof child === 'string') {
                    return child;
                }
                return deserializeComp(child, resolver);
            });
        } else {
            result[key] = prop;
        }
        return result;
    }, {} as Record<string, unknown>);

    if (index) {
        props.key = index;
    }

    const jsx = {
        ...React.createElement<Record<string, unknown>>(main, {
            ...props,
        }),
    } as ReactElement;
    return {
        ...jsx,
        name: resolveComponent(resolver, jsx.type),
    };
};

export const deserializeNode = (data: SerializedNode, designSettings: DesignSettings, account: Account, resolver: Resolver): Omit<NodeData, 'event'> => {
    const { type: Comp, props: Props, ...nodeData } = data;

    const isCompAUserComponent = Comp !== undefined && (Comp as { resolvedName?: string }).resolvedName !== undefined;

    invariant(
        isCompAUserComponent,
        ERROR_DESERIALIZE_COMPONENT_NOT_IN_RESOLVER.replace('%displayName%', data.displayName).replace(
            '%availableComponents%',
            Object.keys(resolver).join(', '),
        ),
    );

    const { type, name, props } = deserializeComp(data, resolver) as unknown as NodeData;

    const { parent, displayName, nodes, hidden } = nodeData;

    const linkedNodes = nodeData.linkedNodes || nodeData._childCanvas;
    
    return {
        type,
        name,
        displayName: displayName || name,
        props: {...props, designSettings, account},
        hidden,
        parent,
        linkedNodes: linkedNodes || {},
        nodes: nodes || [],
    };
};
